/**
 * SubAccountPage — admin CRUD for master → sub-account relationships.
 *
 * Features:
 * - DataTable of all sub-account relationships (filterable by master_id)
 * - Link modal: assign a user as sub-account of a master
 * - Unlink action: remove a sub-account relationship
 * - RBAC gating on link/unlink actions
 */
import { useState } from 'react';
import { Button, Modal, Form, InputNumber, Space, Tag, App } from 'antd';
import { PlusOutlined, DisconnectOutlined } from '@ant-design/icons';
import { DataTable } from '../../components/DataTable';
import { api } from '../../api/client';
import { useAuthStore } from '../../store/auth';

interface SubAccountRow {
  id: number;
  username: string;
  phone: string;
  level_code: string;
  status: string;
  master_id: number | null;
  master_username: string | null;
  create_time: string;
}

export default function SubAccountPage() {
  const { message } = App.useApp();
  const hasNode = useAuthStore((s) => s.hasNode);
  const [masterFilter, setMasterFilter] = useState<number | undefined>();
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkForm] = Form.useForm();
  const [refreshKey, setRefreshKey] = useState(0);

  const canLink = hasNode('sub_account.link');
  const canUnlink = hasNode('sub_account.unlink');

  const handleLink = async () => {
    try {
      const values = await linkForm.validateFields();
      await api.post('/api/admin/sub-accounts/link', values);
      message.success('子账户绑定成功');
      setLinkOpen(false);
      linkForm.resetFields();
      setRefreshKey((k) => k + 1);
    } catch (e: any) {
      message.error(e?.response?.data?.msg || e.message || '绑定失败');
    }
  };

  const handleUnlink = async (subUserId: number) => {
    try {
      await api.post('/api/admin/sub-accounts/unlink', { sub_user_id: subUserId });
      message.success('子账户解绑成功');
      setRefreshKey((k) => k + 1);
    } catch (e: any) {
      message.error(e?.response?.data?.msg || e.message || '解绑失败');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 80 },
    { title: '子账户', dataIndex: 'username', width: 140 },
    { title: '手机号', dataIndex: 'phone', width: 140 },
    {
      title: '等级',
      dataIndex: 'level_code',
      width: 100,
      render: (v: string) => <Tag>{v}</Tag>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (v: string) => (
        <Tag color={v === 'active' ? 'green' : 'red'}>{v}</Tag>
      ),
    },
    { title: '主账户 ID', dataIndex: 'master_id', width: 100 },
    { title: '主账户', dataIndex: 'master_username', width: 140 },
    {
      title: '创建时间',
      dataIndex: 'create_time',
      width: 180,
      render: (v: string) => v?.replace('T', ' ').slice(0, 19),
    },
    ...(canUnlink
      ? [
          {
            title: '操作',
            width: 100,
            render: (_: any, row: SubAccountRow) => (
              <Button
                size="small"
                danger
                icon={<DisconnectOutlined />}
                onClick={() =>
                  Modal.confirm({
                    title: '确认解绑',
                    content: `确定要将 ${row.username} (ID: ${row.id}) 从主账户 ${row.master_username} 解绑吗？`,
                    onOk: () => handleUnlink(row.id),
                  })
                }
              >
                解绑
              </Button>
            ),
          },
        ]
      : []),
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <InputNumber
          placeholder="按主账户 ID 筛选"
          style={{ width: 180 }}
          value={masterFilter}
          onChange={(v) => setMasterFilter(v ?? undefined)}
          min={1}
        />
        {canLink && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setLinkOpen(true)}>
            绑定子账户
          </Button>
        )}
      </Space>

      <DataTable<SubAccountRow>
        queryKey={['admin-sub-accounts', masterFilter, refreshKey]}
        endpoint="/api/admin/sub-accounts"
        extraParams={{ master_id: masterFilter }}
        columns={columns}
        rowKey="id"
      />

      {/* Link modal */}
      <Modal
        title="绑定子账户"
        open={linkOpen}
        onCancel={() => {
          setLinkOpen(false);
          linkForm.resetFields();
        }}
        onOk={handleLink}
        okText="确认绑定"
      >
        <Form form={linkForm} layout="vertical">
          <Form.Item
            name="master_id"
            label="主账户 ID"
            rules={[{ required: true, message: '请输入主账户 ID' }]}
          >
            <InputNumber style={{ width: '100%' }} min={1} placeholder="输入主账户用户 ID" />
          </Form.Item>
          <Form.Item
            name="sub_user_id"
            label="子账户 ID"
            rules={[{ required: true, message: '请输入子账户 ID' }]}
          >
            <InputNumber style={{ width: '100%' }} min={1} placeholder="输入子账户用户 ID" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
